import { useMemo, useState } from 'react';
import { Container, Card, Accordion, Alert, Row, Col } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/common/PageMeta';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { usePublicPathLocale } from '../hooks/usePublicPathLocale';
import { HELP_SYSTEM_CARDS, HELP_USAGE_GUIDES } from '../config/helpPage.config';
import { isLaboSukePublicEnabled } from '../utils/config';
import HelpSystemIndex from '../components/help/HelpSystemIndex';
import HelpUsageGuideItem from '../components/help/HelpUsageGuideItem';
import HelpFaqSection from '../components/help/HelpFaqSection';
import HelpSupportSection from '../components/help/HelpSupportSection';
import HelpTableOfContents from '../components/help/HelpTableOfContents';
import HelpPageStructuredData from '../components/help/HelpPageStructuredData';
import { localePublicPagePath, SITE_URL } from '../constants/siteSeo';
import '../components/help/helpPage.css';

export default function Help() {
  const { t } = useTranslation();
  const location = useLocation();
  const pathLocale = usePublicPathLocale();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const isLoggedIn = Boolean(user);
  const laboSukePublic = isLaboSukePublicEnabled();
  // `/help` は ja / x-default 正規（/ja/help は App ルートで Navigate）
  const pathNorm = location.pathname.replace(/\/+$/, '') || '/';
  const isBareHelpPath = pathNorm === '/help';

  const visibleCards = useMemo(
    () =>
      HELP_SYSTEM_CARDS.filter((card) => {
        if (card.adminOnly && !isAdmin) return false;
        if (card.id === 'labo_suke' && !laboSukePublic) return false;
        return true;
      }),
    [isAdmin, laboSukePublic],
  );
  const visibleGuides = useMemo(
    () =>
      HELP_USAGE_GUIDES.filter((guide) => {
        if (guide.adminOnly && !isAdmin) return false;
        if (guide.id === 'labo_suke' && !laboSukePublic) return false;
        return true;
      }),
    [isAdmin, laboSukePublic],
  );
  const [activeGuideKey, setActiveGuideKey] = useState<string | null>(null);

  const helpPageUrl = `${SITE_URL}${localePublicPagePath(pathLocale, 'help')}`;
  const structuredItems = useMemo(
    () =>
      visibleCards.map((card) => ({
        name: t(card.titleKey),
        description: t(card.descKey),
        url: `${SITE_URL}${card.path}`,
      })),
    [visibleCards, t],
  );

  return (
    <>
      <PageMeta
        titleKey="help.title"
        descriptionKey="help.meta_description"
        keywordsKey="help.meta_keywords"
        path="help"
        localeAware={!isBareHelpPath}
        localeAlternates={isBareHelpPath}
        llmsDescribedBy
      />
      <HelpPageStructuredData pageUrl={helpPageUrl} items={structuredItems} />
      <Container className="mt-4 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1>
              <i className="fas fa-question-circle text-primary me-2" />
              {t('help.title')}
            </h1>
            <p className="text-muted mb-0">{t('help.subtitle')}</p>
          </div>
          <Link
            to={isLoggedIn ? '/dashboard' : '/'}
            className="btn btn-outline-secondary"
          >
            <i className="fas fa-arrow-left me-2" />
            {t(isLoggedIn ? 'help.back_to_dashboard' : 'help.back_to_home')}
          </Link>
        </div>

        <Row>
          <Col lg={3} className="d-none d-lg-block">
            <HelpTableOfContents />
          </Col>
          <Col lg={9}>
            <div className="d-lg-none mb-3">
              <HelpTableOfContents />
            </div>

            <Alert variant="info" className="mb-4">
              <i className="fas fa-info-circle me-2" />
              {t('help.context_help_note')}
            </Alert>

            <Card className="mb-4" id="help-quick-start">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-play-circle me-2" />
                  {t('help.quick_start_title')}
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted">{t('help.quick_start_desc')}</p>
                <ol className="mb-0">
                  <li>{t('help.quick_start_step1')}</li>
                  <li>{t('help.quick_start_step2')}</li>
                  <li>{t('help.quick_start_step3')}</li>
                </ol>
              </Card.Body>
            </Card>

            <Card className="mb-4" id="help-system-index">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-sitemap me-2" />
                  {t('help.system_index_title')}
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">{t('help.system_index_desc')}</p>
                <HelpSystemIndex cards={visibleCards} />
              </Card.Body>
            </Card>

            <Card className="mb-4" id="help-usage-guide">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-book me-2" />
                  {t('help.usage_guide_title')}
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">{t('help.usage_guide_desc')}</p>
                <Accordion
                  flush
                  activeKey={activeGuideKey}
                  onSelect={(key) => setActiveGuideKey(typeof key === 'string' ? key : null)}
                >
                  {visibleGuides.map((guide, index) => {
                    const eventKey = String(index);
                    return (
                      <HelpUsageGuideItem
                        key={guide.id}
                        guide={guide}
                        eventKey={eventKey}
                        isExpanded={activeGuideKey === eventKey}
                      />
                    );
                  })}
                </Accordion>
              </Card.Body>
            </Card>

            <div id="help-faq">
              <HelpFaqSection />
            </div>
            <div id="help-support">
              <HelpSupportSection />
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
}
